import * as Sentry from '@sentry/browser';
import partition from 'lodash/partition';
import { v4 as uuidv4 } from 'uuid';
import type { ActionContext } from 'vuex';
import type { BatchContext, BatchEntry } from '_shared_/UI/AddOns/batch/batchPlan';
import { addOnStrings, fill } from '_shared_/UI/AddOns/addOnStrings.js';
import { checkImages } from '_shared_/UI/AddOns/batch/batchImages';
import { announceToScreenReader } from '_shared_/js/utils/screenReaderAnnounce.js';
import { AddOnRefused, addOnsAPI, storeBatchChunks } from '../communication/addOns';
import { hostRecord, localRecord } from './addOnRecords';
import { addOnWriteQueueOf } from './addOnWriteQueue';

const RETRY_DELAY_MS = 2_000;

/** A row the host never stored. */
export interface BatchFailure {
    line: number;
    title: string;
    /** The host's own words, or null when there are none to show. */
    message: string | null;
}

/** A stored piece whose image did not load in this browser, so the host's download will likely fail too. */
export interface BatchImageIssue {
    line: number;
    title: string;
}

export interface BatchRun {
    phase: 'creating' | 'done';
    created: number;
    /** A stored piece still waits for the image the host fetches after answering. */
    imagesFollow: boolean;
    total: number;
    failures: BatchFailure[];
    imageIssues: BatchImageIssue[];
}

interface State {
    ui: {
        addOnBatch: { dialogOpened: boolean; run: BatchRun | null };
        features: { addOns: { batch: boolean } };
    };
    localizations: Record<string, unknown>;
    lang: string;
    isFixedLayout: boolean;
    epub: { spine: unknown };
    reader: { pagination: { total: number } };
    epubViewer?: {
        printedPageHref(page: number, hostPages: { spine: unknown; totalPages: number }): string | null;
        fixedLayoutPageHref(page: number): string | null;
    };
}

type Context = ActionContext<State, State>;

const failureOf = (entry: BatchEntry, message: string | null): BatchFailure => ({
    line: entry.line,
    title: entry.record.title ?? '',
    message,
});

const reportUnexpected = (error: unknown) => Sentry.captureException(error, { tags: { feature: 'add-ons' } });

/** A refusal is the operator's to read. Anything else is ours to look into. */
function messageOf(error: unknown): string | null {
    if (error instanceof AddOnRefused) return error.message;

    reportUnexpected(error);
    return null;
}

/** With no host, the reader keeps the pieces as the host would have answered them. */
async function answerFor(chunk: BatchEntry[]) {
    const records = chunk.map(entry => entry.record);
    let answer;
    try {
        answer = await addOnsAPI.storeBatch(records);
    } catch (error) {
        // A refusal would only come back the same.
        if (error instanceof AddOnRefused) throw error;

        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
        answer = await addOnsAPI.storeBatch(records);
    }

    return (
        answer ?? {
            created: records.map(record => ({ uuid: record.uuid, addOn: localRecord(record) })),
            failed: [],
        }
    );
}

/**
 * Each entry of the chunk ends up stored or among the failures, and only then
 * does `settled` hear of it. A stored entry whose image the host has yet to
 * fetch joins `awaitingImage`.
 */
async function storeChunk(
    { commit }: Context,
    chunk: BatchEntry[],
    settled: Set<string>,
    awaitingImage: BatchEntry[]
): Promise<void> {
    const pending = new Map(chunk.map(entry => [entry.record.uuid, entry]));
    const take = (uuid: string) => {
        const entry = pending.get(uuid);
        pending.delete(uuid);
        if (entry) settled.add(uuid);
        return entry;
    };

    let answer;
    try {
        answer = await answerFor(chunk);
    } catch (error) {
        const message = messageOf(error);
        chunk.forEach(entry => take(entry.record.uuid));
        commit(
            'addAddOnBatchFailures',
            chunk.map(entry => failureOf(entry, message))
        );
        return;
    }

    // Counted as each piece lands, so a later piece that breaks the run leaves the count true.
    for (const { uuid, addOn } of answer.created ?? []) {
        if (!pending.has(uuid) || !addOn) continue;

        const stored = hostRecord(addOn);
        commit('addAddOn', stored);
        const entry = take(uuid)!;
        if (entry.record.imageUrl && !stored.imageUrl) awaitingImage.push(entry);
        commit('countAddOnBatchCreated');
    }

    const failures = (answer.failed ?? []).flatMap(({ uuid, message }) => {
        const entry = take(uuid);
        return entry ? [failureOf(entry, message)] : [];
    });
    // A piece the answer names nowhere was not stored, as far as this reader can tell.
    failures.push(...[...pending.keys()].map(uuid => failureOf(take(uuid)!, null)));

    if (failures.length > 0) commit('addAddOnBatchFailures', failures);
}

/** The resource a page lives in, which an EPUB page anchor needs. A PDF page needs none. */
function hrefForPageOf(state: State, getters: Record<string, unknown>): BatchContext['hrefForPage'] {
    if (!getters.isEpub) return undefined;
    if (state.isFixedLayout) return page => state.epubViewer?.fixedLayoutPageHref(page) ?? null;

    const hostPages = { spine: state.epub.spine, totalPages: state.reader.pagination.total };
    return page => state.epubViewer?.printedPageHref(page, hostPages) ?? null;
}

export const addOnBatchGetters = {
    canBatchAddOns(state: State, getters: Record<string, unknown>): boolean {
        return Boolean(getters.canEditAddOns) && state.ui.features.addOns.batch;
    },
    addOnBatchRun(state: State): BatchRun | null {
        return state.ui.addOnBatch.run;
    },
    addOnBatchRunning(state: State): boolean {
        return state.ui.addOnBatch.run != null && state.ui.addOnBatch.run.phase !== 'done';
    },
    /** What the plan checks every row against: the pieces already here, the pages, and where a page is. */
    addOnBatchContext(state: State, getters: Record<string, unknown>): BatchContext {
        const embedHosts = (getters.richTextEmbedHosts as { all?: string[] } | undefined)?.all ?? [];

        return {
            totalPages: (getters.pluginTotalPages as number) || null,
            embedHosts,
            existing: getters.visibleAddOns as BatchContext['existing'],
            hrefForPage: hrefForPageOf(state, getters),
            newUuid: uuidv4,
        };
    },
};

export const addOnBatchActions = {
    openAddOnBatchDialog({ getters, commit }: Context): void {
        if (!getters.canBatchAddOns || getters.addOnBatchRunning) return;

        commit('setAddOnBatchDialogOpened', true);
    },
    closeAddOnBatchDialog({ commit }: Context): void {
        commit('setAddOnBatchDialogOpened', false);
    },
    /**
     * Creates the drafts a plan accepted, a chunk at a time in the editor's
     * write queue, so an open form's saves go out between two chunks rather
     * than racing them. The pieces show in the list as each chunk lands, and
     * their images once the host has fetched them. Nobody waits for it: the
     * reader stays usable, and the panel's footer tells how far it got.
     */
    async startAddOnBatch(
        context: Context,
        { entries, unreachable: checked = null }: { entries: BatchEntry[]; unreachable?: string[] | null }
    ): Promise<void> {
        const { state, getters, commit } = context;
        if (!getters.canBatchAddOns || getters.addOnBatchRunning || entries.length === 0) return;

        const strings = addOnStrings(state.localizations, state.lang);
        commit('startAddOnBatchRun', { total: entries.length });
        announceToScreenReader(fill(strings.batchAnnounceStarted, { count: entries.length }));

        // The host fetches each image only after answering and cannot tell this reader how it went,
        // so the browser tries them while the writes go out, unless the preview already did.
        const unreachable = new Set<string>(checked ?? []);
        const imagesChecked = checked
            ? Promise.resolve()
            : checkImages(
                  entries.flatMap(entry => entry.record.imageUrl ?? []),
                  found => found.forEach(url => unreachable.add(url))
              ).catch(reportUnexpected);

        const queue = addOnWriteQueueOf(state);
        const settled = new Set<string>();
        const awaitingImage: BatchEntry[] = [];
        try {
            for (const chunk of storeBatchChunks(entries, entry => entry.record)) {
                await queue.enqueue(() => storeChunk(context, chunk, settled, awaitingImage));
            }
        } catch (error) {
            reportUnexpected(error);
            const unsettled = entries.filter(entry => !settled.has(entry.record.uuid));
            if (unsettled.length > 0)
                commit(
                    'addAddOnBatchFailures',
                    unsettled.map(entry => failureOf(entry, null))
                );
        } finally {
            await imagesChecked;
            const [issues, follow] = partition(awaitingImage, entry => unreachable.has(entry.record.imageUrl!));
            commit('finishAddOnBatchRun', {
                imagesFollow: follow.length > 0,
                imageIssues: issues.map(entry => ({ line: entry.line, title: entry.record.title ?? '' })),
            });
        }

        const { created, failures, imageIssues } = state.ui.addOnBatch.run!;
        const problems = failures.length + imageIssues.length;
        announceToScreenReader(
            fill(problems ? strings.batchAnnounceDoneWithFailures : strings.batchDone, { created, failed: problems }),
            { priority: problems ? 'assertive' : 'polite' }
        );
    },
    dismissAddOnBatch({ getters, commit }: Context): void {
        if (getters.addOnBatchRunning) return;

        commit('clearAddOnBatchRun');
    },
};

export const addOnBatchMutations = {
    setAddOnBatchSupported(state: State, { supported }: { supported: boolean }): void {
        state.ui.features.addOns.batch = supported;
    },
    setAddOnBatchDialogOpened(state: State, opened: boolean): void {
        state.ui.addOnBatch.dialogOpened = opened;
    },
    startAddOnBatchRun(state: State, { total }: { total: number }): void {
        state.ui.addOnBatch.run = {
            phase: 'creating',
            created: 0,
            imagesFollow: false,
            total,
            failures: [],
            imageIssues: [],
        };
    },
    countAddOnBatchCreated(state: State): void {
        state.ui.addOnBatch.run!.created += 1;
    },
    addAddOnBatchFailures(state: State, failures: BatchFailure[]): void {
        state.ui.addOnBatch.run!.failures.push(...failures);
    },
    finishAddOnBatchRun(
        state: State,
        { imagesFollow, imageIssues }: Pick<BatchRun, 'imagesFollow' | 'imageIssues'>
    ): void {
        Object.assign(state.ui.addOnBatch.run!, { phase: 'done', imagesFollow, imageIssues });
    },
    clearAddOnBatchRun(state: State): void {
        state.ui.addOnBatch.run = null;
    },
};
